import * as React from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { StepLabel } from '@mui/material';
import HealthAreaStep from './HealthAreaStep';
import ManageDiseasesStep from './ManageDiseasesStep';
import { handleCreateDiseaseBurden, IDiseaseItem } from '../../../api/mosaic/health_area';
import { useToast } from '../../shared/Toast';
import theme from '../../../utils/theme';
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from '../../shared/ModalWrapper';

const steps = [
  'Health Area Category',
  'Disease Burden',
];

const initialFormData = {
  name: "",
  icode: "",
  id: undefined,
  diseases: [],
};

interface IBase3StepModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

const Base3StepModal: React.FC<IBase3StepModalProps> = ({
  isOpen,
  handleClose,
}) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = React.useState<{
    name: string;
    icode: string;
    id?: number;
    diseases: IDiseaseItem[];
  }>({
    name: "",
    icode: "",
    id: undefined,
    diseases: [],
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const handleNext = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = "Health Area Category Name is required";
    }
    if (!formData.icode.trim()) {
      newErrors.icode = "Health Area Category icode is required";
    }
    if (!formData.diseases.length && activeStep === 1) {
      newErrors.diseases = "At least one Disease Burden is required";
    }

    try {
      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        if (activeStep === 1) {
          setIsLoading(true);
          await handleCreateDiseaseBurden(formData);
          onClose();
          setIsLoading(false);
          showSuccess("Disease burden created successfully");
        }
      }
    } catch (err) {
      setIsLoading(false);
      showError("Error creating disease burden");
      console.error(err);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onClose = () => {
    handleClose();
    setActiveStep(0);
    setFormData(initialFormData);
  };

  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <HealthAreaStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case 1:
        return (
          <ManageDiseasesStep
            formData={formData}
            setFormData={setFormData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ModalWrapper
      open={isOpen}
      onClose={onClose}
      title={steps[activeStep] || "Health Area"}
      maxWidth="sm"
      actions={
        activeStep === 0 ? (
          <Button variant="contained" onClick={handleNext} sx={modalSubmitButtonSx}>
            Next
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={handleBack} disabled={isLoading} sx={modalCancelButtonSx}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext} disabled={isLoading} sx={modalSubmitButtonSx}>
              {isLoading ? "Saving..." : "Finish"}
            </Button>
          </>
        )
      }
    >
      <Stepper
        activeStep={activeStep}
        sx={{
          mb: 2,
          "& .MuiStepIcon-root.Mui-active": { color: theme.palette.brand.primary },
          "& .MuiStepIcon-root.Mui-completed": { color: theme.palette.brand.success },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep < steps.length ? (
        renderStep(activeStep)
      ) : (
        <Typography sx={{ textAlign: 'center', py: 3, color: "#878787" }}>
          Saving...
        </Typography>
      )}
    </ModalWrapper>
  );
};

export default Base3StepModal;
